import type { FC } from 'react';
import React, { useState } from 'react';
import cx from 'classnames';

import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import { MonetaryValue } from 'Components/pages/Wallet/UI/MonetaryValue';
import { Popover } from 'Storybook/core/Popover';
import type { VehicleAccountListItem } from 'Types/vehicle';
import { applyCypressData } from 'Utilities/helpers';

import { ViewSwitch } from './views/ViewSwitch';
import { getValueToDisplay, isVehiclePriceFallback } from './PurchasesVehicleListValueCell.helpers';

import styles from './PurchasesVehicleListValueCell.module.scss';

type PurchasesVehicleListValueCellProps = {
	containerClass: string;
	cypressId: string;
	onOpenChangeSideEffect: (isOpen: boolean) => void;
} & Pick<
	VehicleAccountListItem,
	| 'brokerFeeAmount'
	| 'finalOfferAmount'
	| 'totalPaymentAmount'
	| 'transportFeeAmount'
	| 'hasFees'
	| 'userBid'
	| 'mechanicalGuaranteeFeeAmount'
	| 'mechanicalGuaranteePromotionalRate'
>;

const PurchasesVehicleListValueCell: FC<PurchasesVehicleListValueCellProps> = ({
	brokerFeeAmount,
	containerClass,
	cypressId,
	finalOfferAmount,
	hasFees,
	mechanicalGuaranteeFeeAmount,
	mechanicalGuaranteePromotionalRate,
	onOpenChangeSideEffect,
	totalPaymentAmount,
	transportFeeAmount,
	userBid,
}) => {
	const { maximumBid, type, value } = userBid || {};

	const [open, setOpen] = useState(false);

	const onOpenChangeHandler = (isOpen: boolean) => {
		setOpen(isOpen);
		onOpenChangeSideEffect(isOpen);
	};

	const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();

		onOpenChangeSideEffect(true);
		setOpen(true);
	};

	const labelValue = getValueToDisplay(hasFees, value, totalPaymentAmount);

	const label = isVehiclePriceFallback(labelValue) ? (
		labelValue
	) : (
		<MonetaryValue value={labelValue} variant="regular" />
	);

	return (
		<div className={cx(containerClass)}>
			<span className={styles.popoverWrapper}>
				<Popover
					modal
					content={
						<ViewSwitch
							{...{
								brokerFeeAmount,
								finalOfferAmount,
								hasFees,
								maximumBid,
								mechanicalGuaranteeFeeAmount,
								mechanicalGuaranteePromotionalRate,
								totalPaymentAmount,
								transportFeeAmount,
								type,
								value,
							}}
						/>
					}
					contentAlign="end"
					contentClassName={styles.popoverContent}
					contentOnClick={(event: React.MouseEvent<HTMLDivElement>) => {
						event.preventDefault();
					}}
					contentSide="bottom"
					contentSideOffset={12}
					cypressId={`${cypressId}-content`}
					onOpenChange={onOpenChangeHandler}
					open={open}
					trigger={
						<Hyperlink
							{...applyCypressData(cypressId)}
							data-thc-hyperlink
							as="button"
							data-testid="price-popover"
							label={label}
							onClick={onClickHandler}
						/>
					}
				/>
			</span>
		</div>
	);
};

export default PurchasesVehicleListValueCell;
